import { useEffect, useState } from "react";
import type { Testimonial } from "@tih/shared";
import { useReveal } from "../hooks/useReveal";
import { fetchTestimonials } from "../api/client";
import { TestimonialForm } from "./forms/TestimonialForm";

export function Testimonials() {
  const ref = useReveal<HTMLElement>();
  const [items, setItems] = useState<Testimonial[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchTestimonials()
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(String((e as Error).message ?? e));
          setItems([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loading = items === null;
  const empty = !loading && items.length === 0;

  return (
    <section className="section section-testimonials page-testimonials" id="testimonials" ref={ref}>
      <div className="sec-eyebrow center">
        <em>voices from the room</em>
      </div>
      <h1 className="sec-title center">What attendees say.</h1>
      <p className="lede-serif center testimonials-lede">
        Real stories from worship overnights — shared with permission after our team reviews each
        submission.
      </p>

      {loading ? (
        <div className="page-empty" aria-busy="true">
          Loading stories…
        </div>
      ) : error ? (
        <div className="page-empty page-empty--error" role="alert">
          {error}
        </div>
      ) : empty ? (
        <div className="page-empty">
          <p className="page-empty-title">No published stories yet</p>
          <p>When attendees share what God did in the room, their words will appear here after review.</p>
          <p className="page-empty-hint">Be the first — the form below is open.</p>
        </div>
      ) : (
        <div className="test-grid">
          {items.map((q) => (
            <figure className="test" key={q.id}>
              {q.photoUrls.length > 0 && (
                <div className="test-photos">
                  {q.photoUrls.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt="" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
              <blockquote>{q.body}</blockquote>
              <figcaption>
                <b>{q.name}</b>
                {q.location && <span>{q.location}</span>}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      <div className="testimonial-submit" id="share">
        <div className="sec-eyebrow center">
          <em>your turn</em>
        </div>
        <h2 className="sec-title sec-title-sm center">Share your story</h2>
        <p className="testimonial-cta-note">
          Tell us what God did in the room. You can attach up to three photos as evidence — new
          stories are reviewed before they appear above. No account required.
        </p>
        <div className="testimonial-form-wrap">
          <TestimonialForm />
        </div>
      </div>
    </section>
  );
}
